/**
 * Totalurile comenzii, exact asa cum le-a calculat core-ul.
 *
 * Nu se aduna nimic aici. `total` vine din raspuns si e suma pe care o va plati
 * clientul la usa; recalcularea in browser ar produce, la prima divergenta, un
 * ecran care contrazice bonul fiscal.
 *
 * ADR-004: taxa de livrare e o LINIE, cu cota standard, pe bonul firmei care
 * gateste — nu o suma incasata separat la usa. Se spune pe ecran, fiindca e
 * exact intrebarea pe care o pune clientul cand vede bonul.
 *
 * Bacsisul nu apare nicaieri: `features.tipping` e `false` in contract, ca tip,
 * nu ca setare. Un camp de bacsis aici ar insemna bani incasati de platforma
 * fara traseu fiscal propriu.
 */

import type { OrderTotalsDto } from "@deliveryapp/shared";

import { Price } from "./ui";

function Row({
  label,
  amount,
  emphasis = false,
  hint,
}: {
  label: string;
  amount: number;
  emphasis?: boolean;
  hint?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <span className={emphasis ? "text-base font-semibold text-ink" : "text-sm text-ink-muted"}>
        {label}
        {hint === undefined ? null : <span className="block text-xs text-ink-muted">{hint}</span>}
      </span>
      <Price
        amount={amount}
        className={emphasis ? "text-lg font-bold text-ink" : "text-sm font-medium text-ink"}
      />
    </div>
  );
}

export function TotalsPanel({
  totals,
  sellerCount = 1,
  pickup = false,
}: {
  totals: OrderTotalsDto;
  sellerCount?: number;
  /** Ridicare personala: nu exista livrare, deci nici randul ei. */
  pickup?: boolean;
}) {
  return (
    <section className="rounded-card border border-line bg-surface p-4" aria-labelledby="totaluri-titlu">
      <h2 id="totaluri-titlu" className="text-sm font-semibold uppercase tracking-wide text-ink-muted">
        Sumar plată
      </h2>

      <div className="mt-2 divide-y divide-line">
        <div>
          <Row label="Produse" amount={totals.subtotal} />
          {totals.packagingFee > 0 ? <Row label="Ambalaje" amount={totals.packagingFee} /> : null}
          {totals.smallOrderFee > 0 ? (
            <Row label="Taxă comandă mică" amount={totals.smallOrderFee} />
          ) : null}
          {pickup && totals.deliveryFee === 0 ? null : (
            <Row
              label="Livrare"
              amount={totals.deliveryFee}
              hint={`Se facturează cu TVA ${totals.deliveryFeeVatRate}%, ca linie pe bonul vânzătorului.`}
            />
          )}
          {/* ADR-032: `discountTotal` = voucher/reducere de la suport; `platformSubsidyTotal` = subvenția ofertelor. */}
          {totals.discountTotal > 0 ? <Row label="Voucher / reducere" amount={-totals.discountTotal} /> : null}
          {totals.platformSubsidyTotal > 0 ? (
            <Row
              label="Contribuția platformei"
              amount={-totals.platformSubsidyTotal}
              hint="Scăzută din suma pe care o plătești; bonul poartă suma efectiv datorată de tine."
            />
          ) : null}
        </div>

        <div className="pt-1">
          <Row label="Total de plată" amount={totals.total} emphasis />
        </div>
      </div>

      {sellerCount > 1 ? (
        <p className="mt-3 text-xs text-ink-muted">
          Taxa de livrare este o singură prestație și apare integral pe unul dintre documentele fiscale,
          nu împărțită între ele.
        </p>
      ) : null}
    </section>
  );
}
